import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  List, 
  ListItem, 
  ListItemText, 
  CircularProgress, 
  IconButton,
  Typography,
  ClickAwayListener,
  Popper,
  Paper
} from '@mui/material';
import { Search, Clear, KeyboardArrowDown } from '@mui/icons-material';
import { useSearchableSelectStyles } from './useSearchableSelectStyles';

const SearchableSelect = ({
  label,
  placeholder = 'Select an option...',
  value,
  onChange,
  onSearch,
  options = [],
  loading = false,
  loadingMore = false,
  hasMore = false,
  error,
  helperText,
  disabled = false,
  required = false,
  clearable = true,
  searchable = true,
  maxHeight = 305,
  className,
  style,
  displayKey = 'label',
  valueKey = 'value',
  searchKey = 'label',
  useApiSearch = false, // New prop to control whether to use API search or local filtering
}) => {
  const styles = useSearchableSelectStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update selected option when value changes
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt[valueKey] === value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [value, options, valueKey]);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options?.filter(option =>
        option?.[searchKey]?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, searchKey]);

  // Handle search with debouncing - only for API search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (useApiSearch && onSearch && searchTerm.trim() !== '') {
        onSearch(searchTerm);
      } else if (useApiSearch && onSearch && searchTerm.trim() === '' && options?.length === 0) {
        onSearch('');
      }
    }, 800); // Increased debounce time to reduce API calls

    return () => clearTimeout(timeoutId);
  }, [searchTerm, onSearch, options?.length, useApiSearch]);



  const handleToggle = () => {
    if (disabled) return;
    
    if (!isOpen) {
      setIsOpen(true);
      // Focus search input after dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      setIsOpen(false);
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    onChange?.(option[valueKey], option);
    setIsOpen(false);
    // Don't clear search term - keep it for continued searching
    // setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedOption(null);
    onChange?.(null, null);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const getDisplayText = () => {
    if (selectedOption) {
      return selectedOption[displayKey] || 'Unknown option';
    }
    return placeholder;
  };

  const isError = !!error || !!helperText;

  return (
    <Box sx={styles.container} className={className} style={style}>
      {/* Label */}
      {label && (
        <Typography sx={styles.label}>
          {label}
          {required && <span className="required"> *</span>}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={triggerRef}
        sx={[
          styles.trigger,
          isError && styles.errorTrigger,
          disabled && styles.disabledTrigger,
        ]}
        onClick={handleToggle}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={label || 'Select option'}
      >
        <Box sx={styles.triggerContent}>
          <Typography
            sx={[
              styles.displayText,
              selectedOption && { opacity: 1 }
            ]}
            className={selectedOption ? 'has-value' : ''}
          >
            {getDisplayText()}
          </Typography>
        </Box>

        <Box sx={styles.triggerActions}>
          {clearable && selectedOption && !disabled && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={styles.clearButton}
              aria-label="Clear selection"
            >
              <Clear sx={{ fontSize: '16px' }} />
            </IconButton>
          )}
          <KeyboardArrowDown
            sx={[
              styles.arrowIcon,
              isOpen && { transform: 'rotate(180deg)' }
            ]}
          />
        </Box>
      </Box>

      {/* Dropdown */}
      <Popper
        open={isOpen}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
        ref={dropdownRef}
        modifiers={[
          {
            name: 'sameWidth',
            enabled: true,
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            phase: 'beforeWrite',
            requires: ['computeStyles'],
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper sx={[styles.dropdown, { maxHeight: `${maxHeight}px` }]} elevation={8}>
            {/* Search Input */}
            {searchable && (
              <Box sx={styles.searchContainer}>
                <TextField
                  ref={searchInputRef}
                  fullWidth
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: <Search sx={styles.searchIcon} />,
                  }}
                  sx={styles.searchInput}
                  variant="outlined"
                />
              </Box>
            )}

            {/* Options List */}
            <List sx={styles.optionsList}>
              {loading ? (
                <Box sx={styles.loadingContainer}>
                  <CircularProgress size={20} />
                  <Typography sx={styles.loadingText}>
                    Loading options...
                  </Typography>
                </Box>
              ) : filteredOptions?.length === 0 ? (
                <Box sx={styles.emptyContainer}>
                  <Typography sx={styles.emptyText}>
                    {searchTerm ? 'No results found.' : 'No results found.'}
                  </Typography>
                </Box>
              ) : (
                filteredOptions?.map((option, index) => {
                  const isSelected = selectedOption?.[valueKey] === option[valueKey];
                  // Ensure unique key by using id if available, otherwise combine multiple fields with index
                  const key = option.id || `${option[valueKey] || option[displayKey] || 'unknown'}-${index}-${option.state || option.country || 'unknown'}`;
                  
                  return (
                    <ListItem
                      key={key}
                      sx={[
                        styles.optionItem,
                        isSelected && styles.selectedOption,
                      ]}
                      onClick={() => handleOptionSelect(option)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <ListItemText
                        primary={option[displayKey] || 'Unknown option'}
                        sx={styles.optionText}
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Helper Text */}
      {(helperText || error) && (
        <Typography
          sx={[
            styles.helperText,
            (error || helperText) && styles.errorText
          ]}
        >
          {error || helperText}
        </Typography>
      )}
    </Box>
  );
};

export default SearchableSelect; 